import { ChannelType } from 'components/CampaignCreationModal/Types'
import Styles from './PostPreviewHeader.module.scss'
import { ChannelIcon } from 'components/ChannelIcon/ChannelIcon'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Button } from 'blaze-ui/Button'
import { DateTimePicker } from 'blaze-ui/DateTimePicker/DateTimePicker'
import dayjs from 'dayjs'
import { PostStatusPropertyValue } from 'api/clients/PropertyClient'
import { ArticlePostPreviewActionTypes, useArticlePostPreviewContext } from '../ArticlePostPreviewContext'
import { Spinner } from 'common/Spinner/Spinner'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { useOverlayTriggerState } from '@react-stately/overlays'
import { useEffect, useState } from 'react'
import { IconButton } from 'blaze-ui/IconButton/IconButton'
import { Edit1, Trash2 } from 'blaze-ui/icons'
import { PostStatusDropdown } from 'components/PostStatusDropdown/PostStatusDropdown'
import { PostStatusDropdownButton } from 'components/PostStatusDropdown/PostStatusDropdownButton'
import { UserIntent } from 'components/PostStatusDropdown/Types'
import { usePostProperties } from '../usePostProperties'
import { schemas } from 'components/Wizards/schemas/schemas'

import { ButtonLink } from 'blaze-ui/Button/ButtonLink'
import { useIsChannelPostable } from 'components/PostStatusDropdown/utils'
import { useScheduledPost } from 'hooks/queries/scheduledPosts/useScheduledPost'
import { AssigneeTypeFromAssetType } from 'hooks/useAlmanacFileSubscription'
import { CreateNewPostModal } from 'common/Prose/components/TextInlineFormatBar/components/CreateNewPost/CreateNewPostModal'
import ArrowSwitchHorizontal from 'blaze-ui/icons/20/ArrowSwitchHorizontal'
import { useModals } from 'almanac-ui/Modal'
import { useAuthedArticle } from '../hooks'
import { Can } from 'components/Can'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import cx from 'classnames'
import { BlogPostOutputTour } from 'components/ProductTour/Tours/BlogPostOutputTour'
import { BlogIntegrationSelect } from 'components/ExternalIntegrationSelect/BlogIntegrationSelect'
import { usePaywallGuard } from 'hooks/usePaywallGuard'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import { getBlogPlatformBrandingWithOnboardingSelection } from 'screens/Calendar/WeekViewV2/CalendarEventCard/utils'
import { useBlogIntegrations } from 'hooks/useBlogIntegrations'
import { useCurrentBrandPlan } from 'hooks/queries/brandPlans/useCurrentBrandPlan'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'

export const PostPreviewHeader = () => {
  const { openModal } = useModals()

  const {
    state: { loading, channel, editing, uploadingMediaFilesMetadata },
    dispatch,
    validationErrors,
    persistChanges,
    almanacFile,
  } = useArticlePostPreviewContext()

  const wixEnabled = useFlipperFlag('wix_blog_integration')
  const blogPlatformOnboardingBrandingEnabled = useFlipperFlag('blog_platform_onboarding_branding')
  const { connectedBlogIntegration } = useBlogIntegrations()
  const { data: brandPlan } = useCurrentBrandPlan()
  const workspace = useCurrentWorkspace()

  const blogPlatformBranding = channel
    ? getBlogPlatformBrandingWithOnboardingSelection({
        channel,
        externalIntegrations: connectedBlogIntegration ? [connectedBlogIntegration] : [],
        selectedBlogPlatform: brandPlan?.selected_blog_platform,
        featureFlags: {
          wixEnabled,
          blogPlatformOnboardingBrandingEnabled,
        },
      })
    : null

  const guardedHandleCrossPost = usePaywallGuard(() =>
    openModal(CreateNewPostModal, {
      postTitle: almanacFile.title,
      articleChannel: channel,
      scheduledPost,
    }),
  )

  const { data: scheduledPost } = useScheduledPost({ id: almanacFile.scheduled_post_id })

  const assigneeType = AssigneeTypeFromAssetType[almanacFile.asset_type]
  const { postAt, status, updatePostAt, clearPostAt } = usePostProperties({
    id: almanacFile.id,
    type: assigneeType,
  })

  const overlayState = useOverlayTriggerState({})
  const currentWorkspaceId = useCurrentWorkspaceId()
  const authedArticle = useAuthedArticle(almanacFile)
  const { isChannelPostable } = useIsChannelPostable()
  const [localPostAt, setLocalPostAt] = useState(postAt)
  if (!loading) {
    if (postAt && !localPostAt) {
      setLocalPostAt(postAt)
    } else if (!postAt && !localPostAt) {
      setLocalPostAt(dayjs().hour(12).minute(0).toISOString())
    }
  }

  const removePostAt = async () => {
    await clearPostAt()
  }

  useEffect(() => {
    if (!postAt) {
      setLocalPostAt(undefined)
    } else {
      setLocalPostAt(postAt)
    }
  }, [postAt])

  const handleScheduleAndSave = async () => {
    await updatePostAt(localPostAt)
    persistChanges()
    showToastMessage('Post saved!', 'success')
    overlayState.close()
  }

  const saveChanges = async () => {
    await persistChanges()
    showToastMessage('Post saved!', 'success')
  }

  const notPostable = validationErrors.length > 0
  const notSaveable = status?.value === PostStatusPropertyValue.AutoPostScheduled && validationErrors.length > 0

  const dropdownSize = status?.value === PostStatusPropertyValue.Draft ? 'md' : 'sm'

  const isAutopilotUser = workspace?.is_virtual_marketer

  let actions

  if (editing && !notSaveable && status.value !== PostStatusPropertyValue.Draft) {
    actions = [
      <Button
        key="cancel"
        variant="secondary"
        size="sm"
        onPress={() => dispatch({ type: ArticlePostPreviewActionTypes.RESET })}
      >
        Cancel
      </Button>,
      <Button key="save" variant="primary" size="sm" onPress={saveChanges}>
        Save Content
      </Button>,
    ]
  } else {
    actions = [
      channel === ChannelType.BlogPost && <BlogIntegrationSelect key="wordpress" />,
      !isAutopilotUser && (
        <Can I="edit" a={authedArticle} inWorkspace={currentWorkspaceId} key="edit">
          <Button
            variant="secondary"
            size="sm"
            key="new-post"
            onPress={guardedHandleCrossPost}
            frontIcon={ArrowSwitchHorizontal}
          >
            Cross Post
          </Button>
        </Can>
      ),
      <Can I="edit" a={authedArticle} inWorkspace={currentWorkspaceId} key="dropdown">
        <PostStatusDropdown
          inDocView
          disabledKeys={notPostable ? [UserIntent.ScheduleAutoPost, UserIntent.PostNow] : []}
          afterExecute={(intent) => {
            if (intent === UserIntent.ScheduleAutoPost) {
              dispatch({ type: ArticlePostPreviewActionTypes.SET_EDITING, payload: false })
            }
          }}
          onAction={(action) => {
            if (action === UserIntent.EditContent) {
              dispatch({ type: ArticlePostPreviewActionTypes.SET_EDITING, payload: true })
            }
          }}
          assignee={{ id: almanacFile.id, type: assigneeType }}
          size={dropdownSize}
        >
          <div className={cx('social-media-doc-tour-3', 'blog-post-output-tour-3')}>
            <PostStatusDropdownButton
              size="sm"
              variant="secondary"
              withChevron="down"
              status={status?.value}
              isDisabled={uploadingMediaFilesMetadata.length > 0 || scheduledPost?.is_posting}
            />
          </div>
        </PostStatusDropdown>
      </Can>,
    ]
  }

  let title
  if (isChannelPostable(channel) && channel !== ChannelType.BlogPost) {
    title = 'Post Preview'
  } else if (schemas[channel as ChannelType]) {
    title = (schemas[channel as ChannelType].meta()?.title as string) || 'Document'
  } else {
    title = 'Document'
  }

  return (
    <div className={cx(Styles.headerContainer, { [Styles.isBlog]: channel === ChannelType.BlogPost })}>
      <div className={Styles.header}>
        <div className={Styles.headerLeft}>
          <ChannelIcon channel={channel} size={20} blogPlatform={blogPlatformBranding} />
          <Heading level={5} color="var(--dark-80)">
            {title}
          </Heading>
          {postAt && (
            <div className={Styles.timestamp}>
              <span className={Styles.postAt}>{dayjs(postAt).format('ddd, MMM D h:mma z')}</span>
              {status?.value !== PostStatusPropertyValue.Posted && !scheduledPost?.is_posting && (
                <DateTimePicker
                  onClose={overlayState.close}
                  dateTime={localPostAt ? dayjs(localPostAt) : undefined}
                  actions={(close) => (
                    <Button
                      onPress={() => {
                        handleScheduleAndSave()
                        close()
                      }}
                    >
                      Save New Date & Time
                    </Button>
                  )}
                  setDateTime={(datetime) => {
                    setLocalPostAt(datetime?.toISOString())
                  }}
                >
                  <IconButton icon={Edit1} size="xs" variant="ghost" />
                </DateTimePicker>
              )}

              {scheduledPost?.post_url && status?.value === PostStatusPropertyValue.Posted && (
                <ButtonLink to={scheduledPost.post_url} target="_blank" size="sm" variant="ghost">
                  View Post
                </ButtonLink>
              )}
              {status?.value === PostStatusPropertyValue.Draft && !scheduledPost?.is_posting && (
                <IconButton icon={Trash2} size="xs" variant="ghost" onPress={removePostAt} />
              )}
            </div>
          )}
        </div>
        {loading ? (
          <Spinner />
        ) : (
          <div className={Styles.headerRight}>
            <Can I="edit" a={authedArticle} inWorkspace={currentWorkspaceId} key="dropdown">
              <BlogPostOutputTour eligible={channel === ChannelType.BlogPost} />
            </Can>
            {actions}
          </div>
        )}
      </div>
    </div>
  )
}
